import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { HorizontalScrollRow } from '@/ui/common';
import {
  DEFAULT_DAY_FILTER_OFFSET,
  buildDayFilterOptions,
  type DayFilterOffset,
} from './day-filter-config';
import { TimeFilterItem } from './TimeFilterItem';

export interface DayFilterMenuProps {
  /** Controlled day offset selection. */
  value?: DayFilterOffset;
  defaultValue?: DayFilterOffset;
  onValueChange?: (offset: DayFilterOffset) => void;
  className?: string;
}

/**
 * Horizontal day chips for the Today page.
 * Options: Today + next 5 days. Default: Today.
 */
export function DayFilterMenu({
  value,
  defaultValue = DEFAULT_DAY_FILTER_OFFSET,
  onValueChange,
  className,
}: DayFilterMenuProps) {
  const { t, i18n } = useTranslation();
  const [uncontrolled, setUncontrolled] = useState<DayFilterOffset>(defaultValue);
  const selected = value ?? uncontrolled;
  const options = useMemo(
    () =>
      buildDayFilterOptions(new Date(), {
        locale: i18n.language,
        todayLabel: t('Today', { defaultValue: 'Today' }),
        tomorrowLabel: t('Tomorrow', { defaultValue: 'Tomorrow' }),
      }),
    [i18n.language, t],
  );

  const select = (offset: DayFilterOffset) => {
    if (value === undefined) {
      setUncontrolled(offset);
    }
    onValueChange?.(offset);
  };

  return (
    <HorizontalScrollRow
      aria-label={t('Day filter', { defaultValue: 'Day filter' })}
      className={cn('h-8 items-center gap-2 pl-0', className)}
    >
      {options.map((option) => (
        <TimeFilterItem
          key={option.offset}
          label={option.label}
          selected={option.offset === selected}
          onClick={() => select(option.offset)}
        />
      ))}
    </HorizontalScrollRow>
  );
}
